import React, { useEffect, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useConfig from 'client/hooks/useConfig';
import { useFormContext } from '@ff/ui/Form2/Context';
import InputField from '@ff/ui/Form2/Fields/InputField'
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';
import Icon from '@ff/ui/Icon';
import Spinner from '@ff/ui/Spinner';
import Text from '@ff/ui/Text';
import { ZIndexBuilder } from '@ff/ui/utils/ZIndexBuilder';
import url from 'client/utils/url';
import useReferrer from 'client/hooks/queries/useReferrer';
import useReferrerDetail from 'client/hooks/queries/useReferrerDetail';
import debounce from 'lodash/debounce';
import modelCodes from 'client/constants/modelCode';

const borderImageBase64 = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYAgMAAACdGdVrAAAACVBMVEUAAAAAAAD///+D3c/SAAAAAnRSTlMAIE397fAAAAABYktHRAJmC3xkAAAAHUlEQVQI12MIZQBCRgYwQKccIAysFFYNNNEHdiEAhgkFCxuf3mIAAAAASUVORK5CYII=';

const useReferralCode = () => {
  const history = useHistory();
  const qs = useConfig();
  const { REFERRAL_CODE: referralCode='', ...rest } = qs;
  const { setFieldValue } = useFormContext();
  const { referrer, loading } = useReferrer(referralCode as string, {
    skip: !referralCode || referralCode.length < 6,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCheck = useCallback(
    debounce((checkedReferralCode: string) => {
      history.replace(url.getPageNavigationV3('summary', {
        ...rest,
        REFERRALCODE: checkedReferralCode,
      }));
    }, 500, { leading: false, trailing: true }),
    []
  );

  useEffect(() => {
    if(!loading){
      setFieldValue('referralCode', referrer ? referralCode : '');
      setFieldValue('displayedReferralCode', referralCode);
    }

  }, [loading, setFieldValue, referralCode, referrer]);

  return {
    referralCode,
    check: debouncedCheck,
    referrer,
    loading,
  }
}

const zIndices = new ZIndexBuilder({
  referralInputFieldIcon: {
    inputField: {},
  }
}).zIndices;

// Constants for referral code icons
const ICON_CHECKMARK = 'ff-icon-checkmark';
const ICON_CLOSE = 'ff-icon-close';

// Error messages as constants
const ERROR_REFERRAL = 'The referral code is incorrect. Please verify your referral code and re-enter, or delete the referral code.';
const ERROR_OWN_REFERRAL_RULE = 'According to our referral rules, you cannot use your own referral code for preorder. Please use the referral code provided by someone else, or delete the referral code.';

export default function ReferralCodeBox({ modelCode, setIsReferralCodeValid }) {
  const { referralCode, check: checkReferralCode, referrer, loading: loadingReferralCode } = useReferralCode();
  const { referrerDetail, loading: loadingReferrerDetail } = useReferrerDetail();
  const { setFieldValue } = useFormContext();

  const [error, setError] = useState('');
  const [errorReferralRules, setErrorReferralRules] = useState('');
  const [message, setMessage] = useState('');
  const [referralCodeIcon, setReferralCodeIcon] = useState<null | 'ff-icon-close' | 'ff-icon-checkmark'>(null);

  const modelName = Object.keys(modelCodes).find(k => modelCodes[k].fullModelCode === modelCode);
  const points = modelCodes[modelName]?.referralPoints;

  const resetErrorsAndIcon = () => {
    setError('');
    setErrorReferralRules('');
    setReferralCodeIcon(null);
  }

  const setReferralError = useCallback((errorMessage, rulesErrorMessage = '') => {
    setError(errorMessage);
    setErrorReferralRules(rulesErrorMessage);
    setIsReferralCodeValid(false);
    setReferralCodeIcon(ICON_CLOSE);
  }, [setIsReferralCodeValid]);

  useEffect(() => {
    if (loadingReferralCode || loadingReferrerDetail) return;

    // Empty input field
    if (!referralCode) {
      setMessage(`By using the Referral Code to purchase a car, you will receive ${points}F Co-creation Points.`);
      resetErrorsAndIcon();
      setIsReferralCodeValid(null);
      return;
    }

    // Error: Input field contains a referral code, but it is not valid
    if (!referrer) {
      setReferralError(ERROR_REFERRAL);
      return;
    }

    // Error: Prevent users from using their own referrer code
    if (referrerDetail?.referralCode === referralCode) {
      setReferralError('', ERROR_OWN_REFERRAL_RULE);
      return;
    }

    // Valid referral code
    setMessage(`Referral code of ${referrer.name}'s is accepted!\nAfter the delivery of the vehicle, you will receive ${points}F Co-creation Points.`);
    resetErrorsAndIcon();
    setIsReferralCodeValid(true);
    setReferralCodeIcon(ICON_CHECKMARK);

}, [loadingReferralCode, referralCode, referrer, points, loadingReferrerDetail, referrerDetail?.referralCode, setIsReferralCodeValid, setReferralError]);

  const handleReferralCodeChanged = (event) => {
    checkReferralCode(event.target.value);
  }

  const clearReferralCodeInputField = () => {
    setFieldValue('referralCode', '');
    setFieldValue('displayedReferralCode', '');
    resetErrorsAndIcon();
    checkReferralCode('');
  }

  const loading = loadingReferralCode || loadingReferrerDetail;
  const noErrors = !error && !errorReferralRules;
  const validReferrerAndCode = referrer && referralCode && noErrors;

  const referralStatusIcon = loading ? (
    <StyledSpinner>
      <Spinner />
    </StyledSpinner>
  ) : (referralCodeIcon && (
    validReferrerAndCode ? (
      <ReferralInputFieldIcon name={referralCodeIcon} color='#34C759' isClickable={false} />
    ) : (
      <ReferralInputFieldIcon name={referralCodeIcon} color='rgba(0, 0, 0, 0.5)' isClickable={true} onClick={clearReferralCodeInputField} />
    )
  ));

  return (
    <Container>
      <Container2>
        {referralStatusIcon}
        <ReferralCodeInput
          name='displayedReferralCode'
          label='Referral Code'
          disabled={validReferrerAndCode || loading} 
          onChange={handleReferralCodeChanged}
        />
      </Container2>
      <Box>
        {errorReferralRules ? (
          <Message isError>{errorReferralRules}</Message>
        ) : (  
          <Message isError={!!error}>{error || message.split('\n').map(item => <Text key={item} fontSize='12px' marginTop='5px'>{item}</Text>)}</Message>
        )}
      </Box>
    </Container>
  );
}

const StyledSpinner = styled(Box)({
  boxSizing: 'border-box',
  fontSize: '17px',
  marginTop: '0.92em',
  position: 'absolute',
  right: '0.8em',
  zIndex: zIndices['referralInputFieldIcon'],
});

const ReferralInputFieldIcon = styled(Icon)<{ isClickable: boolean }>(({ isClickable }) => ({
  boxSizing: 'border-box',
  cursor: isClickable ? 'pointer' : 'default',
  fontSize: '17px',
  marginTop: '0.92em',
  position: 'absolute',
  right: '0.8em',
  zIndex: zIndices['referralInputFieldIcon'],
}));

const Container = styled(Box)({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const Container2 = styled(Box)({
  position: 'relative',
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
});

const ReferralCodeInput = styled(InputField)({
  height: '44.5px',
  flex: '1',
  textAlign: 'center',
  fontSize: '20px',
  fontWeight: 400,
  lineHeight: '24px',
  padding: '2px 35px 2px 2px',
  zIndex: zIndices['inputField'],

  ":disabled": {
    border: '8px solid transparent',
    borderImageSource: `url(${borderImageBase64})`, //tried to use a svg for borderImage, but it didn't work well.
    borderImageSlice: '8',
    borderImageRepeat: 'round',
  }
});

const Message = styled(Text)<{isError: boolean}>(({isError}) => ({
  position: 'relative',
  top: '-20px',
  fontSize: '12px',
  lineHeight: '14px',
  fontWeight: '400',
  color: isError ? '#BB4949' : '#000000',
}));